import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Tier } from '@foundry/shared';
import { apiFetch } from './client.js';
import type { CallOption, TierCallOption, TierConfigRow } from './types.js';

interface ConfigResponse {
  tierConfig: TierConfigRow[];
  callOptions: CallOption[];
  tierCallOptions: TierCallOption[];
}

export function useTierConfig() {
  return useQuery({
    queryKey: ['config'],
    queryFn: () => apiFetch<ConfigResponse>('/config'),
  });
}

export function useUpdateTierConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tier: Tier; workingHours: number }) =>
      apiFetch<{ config: TierConfigRow }>(`/config/tiers/${input.tier}`, {
        method: 'PATCH',
        body: { workingHours: input.workingHours },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config'] }),
  });
}

export function useUpdateCallOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; label?: string; link?: string }) =>
      apiFetch<{ option: CallOption }>(`/config/call-options/${input.key}`, {
        method: 'PATCH',
        body: {
          ...(input.label !== undefined ? { label: input.label } : {}),
          ...(input.link !== undefined ? { link: input.link } : {}),
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config'] }),
  });
}

export function useSetTierCallOptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tier: Tier; options: { optionKey: string; maxPerMonth: number }[] }) =>
      apiFetch<{ tierCallOptions: TierCallOption[] }>(`/config/tiers/${input.tier}/call-options`, {
        method: 'PUT',
        body: { options: input.options },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config'] }),
  });
}
